<template>
  <div class="tool-detail-page white-theme pollo-layout">
    <div class="container">
      <section id="watermark-tool" class="first-screen">
        <div class="breadcrumb">
          <router-link to="/">{{ $t('modelDetail.breadcrumb.home') }}</router-link>
          <span>&rsaquo;</span>
          <router-link to="/tools">{{ $t('tools.page.allTools') }}</router-link>
          <span>&rsaquo;</span>
          <span>{{ $t('tools.watermark-remover.name') }}</span>
        </div>
        <div class="first-screen-grid">
          <div class="action-column">
            <div v-if="!isLoggedIn" class="login-prompt">
              <div class="login-prompt-content">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" stroke-width="1"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                <h2>{{ $t('tools.detail.loginRequired') }}</h2>
                <p>{{ $t('tools.detail.loginRequiredDesc') }}</p>
                <button class="btn btn-primary" @click="goLogin">{{ $t('brandSeo.toolFreeTrialCta') }}</button>
              </div>
            </div>

            <div v-else class="tool-interface">
        <div class="upload-control-label">{{ $t('tools.watermark-remover.uploadLabel') }}<span>*</span></div>
        <div
          class="tool-upload"
          @click="triggerUpload"
          @dragover.prevent
          @drop.prevent="onDrop"
        >
          <input type="file" ref="fileInput" accept="image/*" style="display:none" @change="handleFile">
          <div v-if="!uploaded" class="upload-placeholder">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" stroke-width="1"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12"/></svg>
            <p>{{ $t('tools.detail.clickUpload') }}{{ $t('tools.fileTypes.image') }}</p>
          </div>
          <div v-else class="upload-preview">
            <div class="preview-box preview-box--image">
              <img v-if="previewSrc" :src="previewSrc" :alt="$t('tools.watermark-remover.name') + ' preview'" class="preview-img clickable-preview" @click.stop="openImage(previewSrc)">
            </div>
            <button class="btn btn-secondary" @click.stop="clearUpload">{{ $t('tools.detail.reupload') }}</button>
          </div>
        </div>
        <p class="upload-spec">{{ $t('tools.watermark-remover.uploadHint') }}</p>

        <div class="tool-options">
          <button class="btn btn-primary btn-lg" :disabled="!uploaded || processing" @click="process">
            <svg v-if="processing" class="spinner" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
            <span>{{ processing ? $t('tools.detail.processing') : $t('brandSeo.toolProcessCta', { action: $t('tools.detail.process') }) }}</span>
          </button>
        </div>

            </div>
          </div>

          <div class="showcase-column">
          <div class="tool-hero">
            <div class="tool-hero-info">
              <span class="tool-icon-lg" aria-hidden="true">✦</span>
              <div>
                <h1>{{ $t('tools.watermark-remover.name') }}</h1>
                <p class="tool-hero-desc">{{ $t('brandSeo.toolDescription', { description: $t('tools.watermark-remover.fullDesc') }) }}</p>
                <div class="hero-chips" aria-hidden="true">
                  <span>JPG · PNG · WebP</span>
                  <span>{{ $t('tools.watermark-remover.aiRepair') }}</span>
                  <span>15MB</span>
                </div>
              </div>
            </div>
          </div>

          <div class="demo-section workspace-panel" v-if="!processing && !processed">
            <h2 class="demo-title">{{ $t('tools.detail.demo') }}</h2>
            <div class="demo-comparison">
              <div class="demo-item">
                <span class="demo-label">{{ $t('tools.detail.before') }}</span>
                <div class="demo-image">
                  <img src="@/assets/images/remove_full_watermark.jpg" alt="Before" class="clickable-preview" @click.stop="openImage($event.currentTarget.src)">
                </div>
              </div>
              <div class="demo-arrow">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </div>
              <div class="demo-item">
                <span class="demo-label">{{ $t('tools.detail.after') }}</span>
                <div class="demo-image">
                  <img src="@/assets/images/remove_full_watermark2.jpg" alt="After" class="clickable-preview" @click.stop="openImage($event.currentTarget.src)">
                </div>
              </div>
            </div>
          </div>

          <div class="processing-panel workspace-panel" v-else-if="processing">
            <div class="workspace-heading">
              <h2>{{ $t('tools.detail.processing') }}</h2>
              <span class="processing-status"><i></i>{{ $t('tools.watermark-remover.aiRepair') }}</span>
            </div>
            <div class="processing-preview">
              <img v-if="previewSrc" :src="previewSrc" alt="Processing preview">
              <div class="processing-overlay">
                <div class="loading-spinner"></div>
                <strong>{{ $t('tools.detail.processing') }}</strong>
              </div>
            </div>
            <div class="processing-track"><span></span></div>
          </div>

          <div class="result-panel workspace-panel" v-else>
            <div class="workspace-heading">
              <h2>{{ $t('tools.detail.after') }}</h2>
              <span class="result-status">✓ {{ $t('tools.watermark-remover.aiRepair') }}</span>
            </div>
            <div class="output-comparison">
              <div class="output-before">
                <span class="output-label">{{ $t('tools.detail.before') }}</span>
                <div class="output-image output-image--fill">
                  <img v-if="previewSrc" :src="previewSrc" alt="Before" class="compare-img clickable-preview" @click.stop="openImage(previewSrc)">
                </div>
              </div>
              <div class="output-after">
                <span class="output-label">{{ $t('tools.detail.after') }}</span>
                <div class="output-image output-image--fill">
                  <img v-if="resultSrc" :src="resultSrc" alt="After" class="compare-img clickable-preview" @click.stop="openImage(resultSrc)">
                </div>
              </div>
            </div>
            <div class="output-actions">
              <button v-if="resultSrc" type="button" class="btn btn-primary" @click="downloadResult">⬇️ {{ $t('tools.detail.download') }}</button>
              <button class="btn btn-secondary" @click="reset">{{ $t('tools.detail.processNew') }}</button>
            </div>
          </div>
          </div>
        </div>
      </section>

      <div class="seo-content-start detail-section feature-section">
        <div class="section-heading">
          <span class="section-kicker">{{ $t('tools.watermark-remover.featuresKicker') }}</span>
          <h2>{{ $t('brandSeo.sectionTitle', { title: $t('tools.watermark-remover.featuresTitle') }) }}</h2>
          <p>{{ $t('tools.watermark-remover.featuresIntro') }}</p>
        </div>
        <a v-if="features[0]" class="section-primary-cta" href="#watermark-tool">{{ features[0].cta }}</a>
        <div class="landing-demo-comparison">
          <div class="landing-demo-half">
            <img src="@/assets/images/remove_full_watermark.jpg" alt="Before" class="clickable-preview" @click.stop="openImage($event.currentTarget.src)">
          </div>
          <div class="landing-demo-half">
            <img src="@/assets/images/remove_full_watermark2.jpg" alt="After" class="clickable-preview" @click.stop="openImage($event.currentTarget.src)">
          </div>
          <span class="landing-demo-divider"></span>
        </div>
        <div class="feature-list">
          <article class="feature-row" v-for="(feature, idx) in features" :key="'feature-' + idx">
            <div class="feature-visual">
              <div class="feature-visual-half feature-visual-half--before">
                <img :src="featureImages[idx].before" :alt="featureImages[idx].label + ' before watermark removal'" class="clickable-preview" @click.stop="openImage(featureImages[idx].before)">
              </div>
              <div class="feature-visual-half feature-visual-half--after">
                <img :src="featureImages[idx].after" :alt="featureImages[idx].label + ' after watermark removal'" class="clickable-preview" @click.stop="openImage(featureImages[idx].after)">
              </div>
              <span class="feature-divider"></span>
            </div>
            <div class="feature-copy">
              <span class="feature-index">0{{ idx + 1 }}</span>
              <h3>{{ feature.title }}</h3>
              <p>{{ feature.desc }}</p>
              <a class="feature-cta" href="#watermark-tool">{{ feature.cta }}</a>
            </div>
          </article>
        </div>
      </div>

      <div class="detail-section use-case-section">
        <div class="section-heading">
          <span class="section-kicker">{{ $t('tools.watermark-remover.useCasesKicker') }}</span>
          <h2>{{ $t('tools.watermark-remover.useCasesTitle') }}</h2>
        </div>
        <div class="use-case-tabs" role="tablist">
          <button
            v-for="(item, idx) in audienceScenes"
            :key="'case-tab-' + idx"
            type="button"
            role="tab"
            :aria-selected="activeUseCaseIndex === idx ? 'true' : 'false'"
            :class="{ active: activeUseCaseIndex === idx }"
            @click="setActiveUseCase(idx)"
          >
            {{ item.label }}
          </button>
        </div>
        <div v-if="activeAudienceScene" class="use-case-showcase" role="tabpanel">
          <div class="use-case-showcase__visual">
            <img
              :src="activeAudienceScene.image"
              :alt="activeAudienceScene.label"
              class="clickable-preview"
              :class="{ 'use-case-showcase__image--top': activeUseCaseIndex === 1 }"
              @click.stop="openImage(activeAudienceScene.image)"
            >
          </div>
          <div class="use-case-showcase__copy">
            <h3>{{ activeAudienceScene.title }}</h3>
            <p>{{ activeAudienceScene.desc }}</p>
          </div>
        </div>
      </div>

      <div class="detail-section how-to-section">
        <div class="section-heading">
          <span class="section-kicker">{{ $t('tools.watermark-remover.howToKicker') }}</span>
          <h2>{{ $t('brandSeo.sectionTitle', { title: $t('tools.watermark-remover.howToTitle') }) }}</h2>
          <p>{{ $t('tools.watermark-remover.howToIntro') }}</p>
        </div>
        <div class="steps">
          <div class="step" v-for="(step, idx) in steps" :key="idx">
            <div class="step-number">{{ idx + 1 }}</div>
            <h3>{{ step.title }}</h3>
            <p>{{ step.desc }}</p>
          </div>
        </div>
      </div>

      <div class="detail-section more-tools-section">
        <h2>{{ $t('tools.detail.moreTools') }}</h2>
        <div class="grid-5">
          <ToolListCard
            v-for="t in otherTools"
            :key="t.id"
            :tool-id="t.id"
            :icon="t.icon"
            :badge="t.badge"
            :to="'/tools/' + t.id"
          />
        </div>
      </div>

      <div class="detail-section faq-section">
        <div class="section-heading">
          <h2>{{ $t('brandSeo.faqTitle', { title: $t('tools.watermark-remover.faqTitle') }) }}</h2>
        </div>
        <div class="faq-list">
          <details v-for="(item, idx) in faqs" :key="'faq-' + idx" class="faq-item">
            <summary>{{ item.q }}</summary>
            <p>{{ item.a }}</p>
          </details>
        </div>
      </div>

      <ToolHowToCta
        :kicker="$t('tools.watermark-remover.howToKicker')"
        :title="$t('brandSeo.sectionTitle', { title: $t('tools.watermark-remover.howToTitle') })"
        :intro="$t('tools.watermark-remover.howToIntro')"
        :steps="steps"
        :cta-label="$t('brandSeo.toolFreeTrialCta')"
        target-id="watermark-tool"
      />
    </div>
    <ImageLightbox :src="lightboxSrc" @close="closeImage" />
  </div>
</template>

<script>
import { processWatermarkWithFallback } from '@/api/watermarkRemover'
import { uploadFile, saveOrUpdateAIHistory, formatCreateDate } from '@/api'
import ImageLightbox from '@/components/ImageLightbox.vue'
import ToolHowToCta from '@/components/ToolHowToCta.vue'

var WATERMARK_MAX_BYTES = 15 * 1024 * 1024
var FEATURE_IMAGES = [
  { label: 'Full-image watermark', before: require('@/assets/images/watermark-examples/full-image-watermark-original.webp'), after: require('@/assets/images/watermark-examples/full-image-watermark-after.webp') },
  { label: 'Timestamp', before: require('@/assets/images/watermark-examples/timestamp-original.webp'), after: require('@/assets/images/watermark-examples/timestamp-after.webp') },
  { label: 'Brand logo', before: require('@/assets/images/watermark-examples/logo-original.webp'), after: require('@/assets/images/watermark-examples/logo-after.webp') },
  { label: 'Draft marks', before: require('@/assets/images/watermark-examples/draft-marks-original.webp'), after: require('@/assets/images/watermark-examples/draft-marks-after.webp') }
]
var WATERMARK_AUDIENCE_IMAGES = [
  require('@/assets/images/watermark-usecases/ecommerce-seller.webp'),
  require('@/assets/images/watermark-usecases/marketing-team.jpg'),
  require('@/assets/images/watermark-usecases/designer-creator.webp'),
  require('@/assets/images/watermark-usecases/fashion-apparel.webp')
]

export default {
  name: 'WatermarkRemoverDetail',
  components: {
    ImageLightbox: ImageLightbox,
    ToolHowToCta: ToolHowToCta
  },
  data() {
    return {
      uploaded: false,
      processing: false,
      processed: false,
      previewSrc: '',
      resultSrc: '',
      lightboxSrc: '',
      featureImages: FEATURE_IMAGES,
      activeUseCaseIndex: 0,
      uploadedFile: null,
      processError: null
    }
  },
  mounted() {
  },
  computed: {
    isLoggedIn() {
      return !!this.$store.state.userInfo
    },
    steps() {
      return [
        { title: this.$t('tools.watermark-remover.steps.upload.title'), desc: this.$t('tools.watermark-remover.steps.upload.desc') },
        { title: this.$t('tools.watermark-remover.steps.remove.title'), desc: this.$t('tools.watermark-remover.steps.remove.desc') },
        { title: this.$t('tools.watermark-remover.steps.download.title'), desc: this.$t('tools.watermark-remover.steps.download.desc') }
      ]
    },
    features() {
      return this.$t('tools.watermark-remover.features')
    },
    audienceLabels() {
      var labels = this.$t('tools.watermark-remover.audienceLabels')
      return Array.isArray(labels) ? labels : []
    },
    // 读取当前语言的场景专属文案，未配置时交由现有功能文案兜底。
    audienceCases() {
      var key = 'tools.watermark-remover.audienceCases'
      if (!this.$i18n.te(key, this.$i18n.locale)) return []
      var cases = this.$t(key)
      return Array.isArray(cases) ? cases : []
    },
    // 组装四个适用场景，并绑定用户指定的标签、文案和图片。
    audienceScenes() {
      var labels = this.audienceLabels
      var cases = this.audienceCases
      var features = Array.isArray(this.features) ? this.features.slice(0, 4) : []
      return WATERMARK_AUDIENCE_IMAGES.map(function(image, index) {
        var feature = features[index] || {}
        var content = cases[index] || feature
        return {
          label: labels[index] || feature.title,
          title: content.title || feature.title,
          desc: content.desc || feature.desc,
          image: image
        }
      })
    },
    activeAudienceScene() {
      return this.audienceScenes[this.activeUseCaseIndex] || this.audienceScenes[0] || null
    },
    // 合并工具专属问答与 Miral AI 免费试用说明。
    faqs() {
      var toolFaqs = this.$t('tools.watermark-remover.faqs')
      var brandFaqs = this.$t('brandSeo.toolFaqs')
      return (Array.isArray(toolFaqs) ? toolFaqs : []).concat(Array.isArray(brandFaqs) ? brandFaqs : [])
    },
    otherTools() {
      return this.$store.state.tools.filter(function(t) {
        return t.id !== 'watermark-remover'
      }).slice(0, 5)
    }
  },
  methods: {
    // 切换去水印适用场景，并同步展示对应的现有示例图。
    setActiveUseCase: function(index) {
      this.activeUseCaseIndex = index
    },
    // 打开图片全屏预览。
    openImage: function(src) {
      this.lightboxSrc = src || ''
    },
    // 关闭图片全屏预览。
    closeImage: function() {
      this.lightboxSrc = ''
    },
    goLogin() {
      this.$store.dispatch('showLoginModal')
    },
    triggerUpload() {
      if (this.$refs.fileInput) this.$refs.fileInput.click()
    },
    onDrop(e) {
      var files = e.dataTransfer && e.dataTransfer.files
      if (files && files[0]) this.readFile(files[0])
    },
    handleFile(e) {
      var input = e.target
      var file = input.files && input.files[0]
      if (!file) {
        if (input) input.value = ''
        return
      }
      this.readFile(file)
      if (input) input.value = ''
    },
    readFile(file) {
      var self = this
      this.processError = null
      if (!file.type || file.type.indexOf('image/') !== 0) {
        this.processError = this.$t('tools.watermark-remover.notImageFile')
        return
      }
      if (file.size > WATERMARK_MAX_BYTES) {
        this.processError = this.$t('tools.watermark-remover.fileTooLarge')
        return
      }
      var reader = new FileReader()
      reader.onload = function(ev) {
        self.previewSrc = (ev.target && ev.target.result) ? String(ev.target.result) : ''
        self.uploadedFile = file
        self.uploaded = !!self.previewSrc
        self.processed = false
        self.resultSrc = ''
      }
      reader.onerror = function() {
        self.processError = self.$t('common.generateFailedContactSupport')
      }
      reader.readAsDataURL(file)
    },
    clearUpload() {
      this.uploaded = false
      this.previewSrc = ''
      this.uploadedFile = null
      this.resultSrc = ''
      this.processed = false
      this.processing = false
      this.processError = null
    },
    reset() {
      this.clearUpload()
    },
    downloadResult() {
      if (!this.resultSrc) return
      var url = this.resultSrc
      var name = this.$t('tools.watermark-remover.downloadName')
      if (url.indexOf('data:') === 0) {
        var a0 = document.createElement('a')
        a0.href = url
        a0.download = name
        document.body.appendChild(a0)
        a0.click()
        document.body.removeChild(a0)
        return
      }
      fetch(url)
        .then(function(r) { return r.blob() })
        .then(function(blob) {
          var u = URL.createObjectURL(blob)
          var a = document.createElement('a')
          a.href = u
          a.download = name
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)
          URL.revokeObjectURL(u)
        })
        .catch(function() {
          window.open(url, '_blank')
        })
    },
    process() {
      var self = this
      if (!this.previewSrc || !this.uploadedFile) return
      this.processing = true
      this.processed = false
      this.processError = null

      var createDate = formatCreateDate(new Date())
      var aiName = this.$t('tools.watermark-remover.name')
      var aiDesc = this.$t('tools.watermark-remover.desc')
      var historyId = null
      var originalUrl = ''

      // 1. 先上传原图到服务器
      uploadFile(this.uploadedFile).then(function(url) {
        originalUrl = url
      }).catch(function() {
        self.processing = false
        self.processError = self.$t('tools.detail.uploadOriginalFailed')
        return Promise.reject('uploadOriginalFailed')
      }).then(function() {
        // 2. 创建历史记录，带上原图URL
        return saveOrUpdateAIHistory({
          aiName: aiName,
          taskId: '',
          aiDesc: aiDesc,
          state: 1,
          aiResultUrl: '',
          image_large_url: originalUrl,
          aiMediaType: 'image',
          createDate: createDate
        })
      }).then(function(hRes) {
        historyId = hRes && (hRes.historyId != null ? hRes.historyId : null)
      }).then(function() {
        return processWatermarkWithFallback(originalUrl)
      }).then(function(result) {
        var resultUrl = result && result.url ? result.url : ''
        if (!resultUrl) {
          return Promise.reject(new Error('No result image was returned'))
        }
        self.processing = false
        self.resultSrc = resultUrl
        self.processed = true
        self.processError = null
        saveOrUpdateAIHistory({
          aiName: aiName,
          historyId: historyId,
          taskId: '',
          aiDesc: aiDesc,
          state: 2,
          aiResultUrl: resultUrl,
          image_large_url: originalUrl,
          aiMediaType: 'image',
          createDate: createDate
        }).catch(function() {})
      }).catch(function(err) {
        if (err === 'uploadOriginalFailed') return
        self.processing = false
        self.processed = false
        self.resultSrc = ''
        self.processError = self.$userFacingError(err, self.$t('common.generateFailedContactSupport'))
        saveOrUpdateAIHistory({
          aiName: aiName,
          historyId: historyId,
          taskId: '',
          aiDesc: aiDesc,
          state: 3,
          message: (err && err.message) ? err.message : '',
          aiResultUrl: '',
          image_large_url: originalUrl,
          aiMediaType: 'image',
          createDate: createDate
        }).catch(function() {})
      })
    }
  }
}
</script>

<style scoped>
@import './ModelDetailStyles.css';

.tool-detail-page { padding: 0 0 80px; background: #fff; overflow-x: hidden; }
.breadcrumb { display: flex; align-items: center; gap: 8px; width: 100%; max-width: 1180px; margin: 0 auto; padding: 0 0 18px; color: #8a949e; font-size: 13px; }
.breadcrumb a { color: #65717c; }
.breadcrumb a:hover { color: #07977d; }
.first-screen { width: calc(100vw - 72px); min-height: 690px; box-sizing: border-box; margin-left: calc(50% - 50vw + 36px); padding: 28px 38px 38px; background: #0d0d12; color: #fff; }
.tool-hero { margin-bottom: 34px; }
.tool-hero-info { max-width: 840px; margin: 0 auto; text-align: center; }
.tool-icon-lg { display: none; }
.tool-hero-info h1 { margin: 0 0 12px; color: #fff; font-size: 40px; line-height: 1.15; font-weight: 800; letter-spacing: -.025em; }
.tool-hero-desc { display: block; max-width: 760px; margin: 0 auto 16px; color: #65717c; font-size: 15px; line-height: 1.7; }
.hero-chips { display: flex; justify-content: center; flex-wrap: wrap; gap: 8px; }
.hero-chips span { padding: 7px 13px; border: 1px solid #dce4e8; border-radius: 999px; background: #fff; color: #53606b; font-size: 12px; font-weight: 700; }
.first-screen-grid { display: grid; grid-template-columns: minmax(390px, .88fr) minmax(500px, 1.12fr); align-items: center; gap: 32px; max-width: 1180px; margin: 0 auto; }
.action-column { min-width: 0; }
.section-heading { max-width: 760px; margin: 0 auto 28px; text-align: center; }
.section-heading h2 { margin: 8px 0 12px; font-size: clamp(28px, 3.5vw, 42px); line-height: 1.2; letter-spacing: -.025em; }
.section-heading p { color: var(--text-secondary); line-height: 1.7; }
.section-kicker { color: var(--accent); font-size: 13px; font-weight: 700; letter-spacing: .08em; text-transform: uppercase; }
.seo-card-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 18px; }
.seo-card { position: relative; overflow: hidden; padding: 30px; border: 1px solid rgba(15, 118, 110, .1); border-radius: 22px; background: rgba(255, 255, 255, .88); box-shadow: 0 14px 40px rgba(29, 50, 63, .07); transition: transform .25s ease, box-shadow .25s ease; }
.seo-card::before { content: '0' counter(seo-card); display: block; width: 42px; height: 42px; margin-bottom: 22px; border-radius: 13px; background: linear-gradient(135deg, #d9fff2, #dbeafe); color: #087b68; font-size: 13px; font-weight: 800; line-height: 42px; text-align: center; }
.seo-card-grid { counter-reset: seo-card; }
.seo-card { counter-increment: seo-card; }
.seo-card:hover { transform: translateY(-4px); box-shadow: 0 20px 50px rgba(29, 50, 63, .12); }
.seo-card h3 { margin: 0 0 10px; font-size: 20px; }
.seo-card p { margin: 0; color: var(--text-secondary); line-height: 1.7; }
.seo-card-grid--compact { grid-template-columns: repeat(3, minmax(0, 1fr)); }
.faq-section { padding: 64px; border: 1px solid rgba(15, 118, 110, .1); border-radius: 30px; background: #fff; box-shadow: 0 20px 60px rgba(29, 50, 63, .07); }
.faq-list { max-width: 860px; margin: 0 auto; }
.faq-item { margin-bottom: 10px; padding: 0 22px; border: 1px solid #e7edf1; border-radius: 15px; background: #fbfcfd; }
.faq-item summary { position: relative; cursor: pointer; padding: 20px 38px 20px 0; font-weight: 750; font-size: 16px; list-style: none; }
.faq-item summary::-webkit-details-marker { display: none; }
.faq-item summary::after { content: '+'; position: absolute; right: 0; top: 15px; width: 30px; height: 30px; border-radius: 50%; background: #e8f8f3; color: #07856f; font-size: 20px; line-height: 28px; text-align: center; }
.faq-item[open] summary::after { content: '−'; }
.faq-item p { color: var(--text-secondary); line-height: 1.7; margin: 0; padding: 0 38px 20px 0; }
/* 与 BackgroundRemover / ImageEnhancer / DocCrop / Handwritten 四页 demo 样式一致 */
.demo-section { min-width: 0; padding: 0; border: 0; border-radius: 16px; background: transparent; box-shadow: none; }
.demo-title { display: none; }
.demo-comparison { position: relative; display: grid; grid-template-columns: 1fr 1fr; gap: 2px; height: 390px; overflow: hidden; border-radius: 16px; background: #202027; }
.demo-comparison::after { content: ''; position: absolute; top: 0; bottom: 0; left: 50%; width: 2px; background: rgba(255, 255, 255, .9); transform: translateX(-1px); }
.demo-item { position: relative; min-width: 0; overflow: hidden; }
.demo-label { position: absolute; z-index: 2; top: 14px; left: 50%; transform: translateX(-50%); padding: 7px 14px; border-radius: 999px; background: rgba(20, 27, 34, .68); color: #fff; font-size: 11px; font-weight: 700; text-transform: uppercase; white-space: nowrap; backdrop-filter: blur(8px); }
.demo-image { width: 100%; height: 100%; overflow: hidden; background: #edf1f3; }
.demo-image img { width: 100%; height: 100%; object-fit: cover; object-position: center 30%; }
.workspace-panel { min-height: 440px; box-sizing: border-box; }
.workspace-heading { display: flex; align-items: center; justify-content: space-between; gap: 16px; margin-bottom: 14px; }
.workspace-heading h2 { margin: 0; color: #fff; font-size: 14px; font-weight: 750; }
.processing-status,
.result-status { display: inline-flex; align-items: center; gap: 7px; padding: 6px 10px; border-radius: 999px; background: #eaf8f3; color: #087d68; font-size: 11px; font-weight: 700; }
.processing-status i { width: 7px; height: 7px; border-radius: 50%; background: #10b981; box-shadow: 0 0 0 5px rgba(16, 185, 129, .12); animation: pulse 1.2s ease-in-out infinite; }
.processing-panel,
.result-panel { padding: 0; border: 0; border-radius: 16px; background: transparent; box-shadow: none; }
.processing-preview { position: relative; height: 348px; overflow: hidden; border-radius: 15px; background: #eef2f4; }
.processing-preview img { width: 100%; height: 100%; object-fit: contain; display: block; }
.processing-overlay { position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 14px; background: rgba(12, 22, 28, .54); color: #fff; backdrop-filter: blur(2px); }
.processing-overlay .loading-spinner { width: 46px; height: 46px; border-color: rgba(255, 255, 255, .26); border-top-color: #63e6be; }
.processing-track { height: 5px; margin-top: 14px; overflow: hidden; border-radius: 999px; background: #2b2b33; }
.processing-track span { display: block; width: 42%; height: 100%; border-radius: inherit; background: linear-gradient(90deg, #10b981, #2dd4bf); animation: processing-slide 1.4s ease-in-out infinite; }
@keyframes pulse { 50% { opacity: .42; } }
@keyframes processing-slide { 0% { transform: translateX(-110%); } 100% { transform: translateX(340%); } }
@media (max-width: 768px) {
  .seo-card-grid,
  .seo-card-grid--compact { grid-template-columns: 1fr; }
}
.demo-arrow { position: absolute; z-index: 3; top: 50%; left: 50%; width: 38px; height: 38px; display: flex; align-items: center; justify-content: center; border: 1px solid rgba(255, 255, 255, .9); border-radius: 50%; background: rgba(255, 255, 255, .92); color: #0b8871; transform: translate(-50%, -50%); box-shadow: 0 6px 18px rgba(18, 33, 42, .15); }
.login-prompt { height: 100%; min-height: 600px; display: flex; align-items: center; justify-content: center; border: 1px solid #292930; border-radius: 14px; padding: 28px; background: #15151b; text-align: center; margin: 0; box-shadow: none; }
.login-prompt-content { max-width: 360px; margin: 0 auto; }
.login-prompt-content svg { display: block; margin: 0 auto; }
.login-prompt h2 { margin: 6px 0 4px; color: #fff; font-size: 20px; }
.login-prompt p { color: #9999a3; font-size: 14px; margin-bottom: 16px; }
.tool-interface { height: 100%; min-height: 600px; box-sizing: border-box; border: 0; border-radius: 0; padding: 0; background: transparent; margin: 0; box-shadow: none; }
.tool-interface { display: flex; flex-direction: column; }
.upload-control-label { margin-bottom: 10px; color: #303945; font-size: 13px; font-weight: 700; }
.upload-control-label span { margin-left: 3px; color: #f43f5e; }
.tool-upload { cursor: pointer; }
.upload-placeholder { display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 230px; padding: 36px 20px; border: 1px solid #303038; border-radius: 10px; background: #202027; transition: border-color .2s, background .2s; }
.upload-placeholder:hover { border-color: #555560; background: #24242c; }
.upload-placeholder p { margin-top: 12px; font-size: 14px; color: #d9d9df; }
.upload-placeholder span { font-size: 12px; color: #8f8f99; }
.upload-spec { min-height: 34px; margin: 10px 0 0; color: #7b8791; font-size: 12px; line-height: 1.55; }
.upload-preview { display: flex; flex-direction: column; align-items: stretch; gap: 12px; max-height: none; }
.preview-box { width: 100%; max-width: 100%; height: 260px; border-radius: 10px; display: flex; align-items: center; justify-content: center; overflow: hidden; background: #202027; }
.preview-box--image { padding: 0; }
.preview-img { max-width: 100%; max-height: 100%; width: auto; height: auto; object-fit: contain; display: block; }
.clickable-preview { cursor: zoom-in; }
.tool-error { color: #f87171; font-size: 13px; margin: 0 0 12px; }
.tool-error--block { text-align: center; margin-bottom: 16px; }
.output-image--fill { padding: 0; overflow: hidden; background: var(--bg-primary); }
.compare-img { width: 100%; height: 100%; object-fit: contain; display: block; min-height: 200px; max-height: 420px; background: var(--bg-primary); }
.tool-options { display: flex; align-items: flex-end; gap: 16px; margin-top: auto; padding-top: 12px; justify-content: stretch; }
.tool-options .btn { width: 100%; min-width: 150px; justify-content: center; text-align: center; }
.tool-options .btn:disabled { cursor: not-allowed; opacity: .48; }
.tool-result { margin-top: 32px; }
.gen-loading { display: flex; flex-direction: column; align-items: center; justify-content: center; height: 200px; gap: 12px; }
.loading-spinner { width: 40px; height: 40px; border: 3px solid var(--border-color); border-top-color: var(--accent); border-radius: 50%; animation: spin 0.8s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
.output-comparison { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.output-label { display: block; font-size: 11px; font-weight: 700; color: #a7a7b0; margin-bottom: 7px; text-transform: uppercase; text-align: center; }
.output-image { height: 328px; border-radius: 15px; display: flex; align-items: center; justify-content: center; }
.output-actions { display: flex; gap: 10px; justify-content: flex-end; margin-top: 14px; }
.output-actions .btn { min-width: 120px; }
.detail-section { margin-bottom: 82px; }
.detail-section h2 { font-size: 24px; font-weight: 700; margin-bottom: 24px; }
.seo-content-start { margin-top: 104px; }
.feature-section { padding-top: 14px; }
.use-case-section { margin-left: -32px; margin-right: -32px; padding: 72px 56px; border-radius: 30px; background: linear-gradient(135deg, #092428, #112235); color: #fff; box-shadow: 0 24px 70px rgba(10, 31, 39, .14); }
.use-case-section .section-kicker { color: #64e9c1; }
.use-case-section .seo-card { border-color: rgba(255, 255, 255, .1); background: rgba(255, 255, 255, .07); box-shadow: none; }
.use-case-section .seo-card::before { background: rgba(99, 235, 194, .12); color: #65efc5; }
.use-case-section .seo-card h3 { color: #fff; }
.use-case-section .seo-card p { color: rgba(255, 255, 255, .68); }
.how-to-section { padding: 20px 0; }
.steps { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 18px; }
.step { position: relative; padding: 30px 24px; border-radius: 20px; background: #fff; text-align: left; box-shadow: 0 14px 40px rgba(29, 50, 63, .07); }
.step-number { width: 44px; height: 44px; border-radius: 14px; background: linear-gradient(135deg, #10b981, #2dd4bf); color: #fff; display: flex; align-items: center; justify-content: center; font-weight: 800; margin: 0 0 22px; box-shadow: 0 8px 20px rgba(16, 185, 129, .22); }
.step h3 { margin-bottom: 6px; }
.step p { font-size: 14px; line-height: 1.7; color: var(--text-muted); }
.more-tools-section { padding-top: 16px; }
.spinner { animation: spin 1s linear infinite; }

/* 首屏保持独立视口，SEO 内容从下一屏开始；视觉风格沿用 Miral 浅色体系 */
.first-screen { min-height: 100vh; display: flex; flex-direction: column; background: radial-gradient(circle at 82% 8%, rgba(45, 212, 191, .12), transparent 30%), #f7f9fc; color: #18202a; }
.first-screen .tool-hero-info h1 { color: #111827; }
.first-screen-grid { flex: 1; align-items: center; }
.first-screen .login-prompt { min-height: 600px; border-color: #e2e8ec; background: #fff; box-shadow: 0 16px 40px rgba(35, 55, 68, .08); }
.first-screen .login-prompt h2 { color: #202731; }
.first-screen .login-prompt p { color: #7c8792; }
.first-screen .tool-interface { padding: 20px; border: 1px solid #dfe7eb; border-radius: 18px; background: rgba(255, 255, 255, .94); color: #202731; box-shadow: 0 14px 36px rgba(35, 55, 68, .07); }
.first-screen .upload-placeholder { border: 1.5px dashed #91d1c3; background: #fff; }
.first-screen .upload-placeholder:hover { border-color: #0aa889; background: #f5fffb; }
.first-screen .upload-placeholder p { color: #4f5b66; }
.first-screen .upload-placeholder span { color: #89939c; }
.first-screen .preview-box { background: #edf1f3; }
.first-screen .demo-section,
.first-screen .processing-panel,
.first-screen .result-panel { padding: 18px; border: 1px solid #e2e8ec; background: #fff; box-shadow: 0 16px 40px rgba(35, 55, 68, .08); }
.first-screen .demo-title { display: block; color: #202731; }
.first-screen .workspace-heading h2 { color: #202731; }
.first-screen .output-label { color: #7c8792; }
.feature-section { max-width: 1120px; margin-left: auto; margin-right: auto; }
.feature-list { display: flex; flex-direction: column; gap: 110px; margin-top: 72px; }
.feature-row { display: grid; grid-template-columns: minmax(0, 1.08fr) minmax(320px, .92fr); align-items: center; gap: 76px; }
.feature-row:nth-child(even) .feature-visual { order: 2; }
.feature-row:nth-child(even) .feature-copy { order: 1; }
.feature-visual { position: relative; display: grid; grid-template-columns: 1fr 1fr; height: 390px; overflow: hidden; border-radius: 24px; background: #edf1f3; box-shadow: 0 20px 55px rgba(31, 50, 63, .13); }
.feature-visual-half { min-width: 0; overflow: hidden; }
.feature-visual-half img { width: 100%; height: 100%; object-fit: cover; object-position: center 28%; }
.feature-divider { position: absolute; top: 0; bottom: 0; left: 50%; width: 2px; background: rgba(255, 255, 255, .92); transform: translateX(-1px); }
.feature-copy { max-width: 440px; }
.feature-index { color: #0a9c82; font-size: 13px; font-weight: 800; letter-spacing: .12em; }
.feature-copy h3 { margin: 14px 0 16px; color: #18202a; font-size: clamp(28px, 3vw, 40px); line-height: 1.18; letter-spacing: -.025em; }
.feature-copy p { margin: 0 0 26px; color: #68737e; font-size: 16px; line-height: 1.8; }
.feature-cta { display: inline-flex; align-items: center; justify-content: center; min-height: 46px; padding: 0 24px; border: 1px solid #0a9c82; border-radius: 999px; color: #087d68; font-size: 14px; font-weight: 750; transition: color .2s, background .2s; }
.feature-cta:hover { color: #fff; background: #0a9c82; }
@media (max-width: 768px) {
  .tool-detail-page { padding-top: 20px; }
  .first-screen { padding: 12px 0 0; }
  .tool-hero { margin-bottom: 24px; }
  .tool-hero-info h1 { font-size: 36px; }
  .tool-hero-desc { font-size: 15px; }
  .first-screen-grid { grid-template-columns: 1fr; }
  .login-prompt,
  .tool-interface { min-height: 320px; padding: 18px; }
  .upload-placeholder { min-height: 220px; padding: 36px 20px; }
  .demo-section { padding: 16px; }
  .demo-comparison { height: 360px; }
  .seo-content-start { margin-top: 70px; }
  .feature-list { gap: 64px; margin-top: 48px; }
  .feature-row { grid-template-columns: 1fr; gap: 28px; }
  .feature-row:nth-child(even) .feature-visual,
  .feature-row:nth-child(even) .feature-copy { order: initial; }
  .feature-visual { height: 300px; }
  .use-case-section { margin-left: 0; margin-right: 0; padding: 52px 20px; border-radius: 24px; }
  .faq-section { padding: 48px 18px; border-radius: 24px; }
  .steps { grid-template-columns: 1fr; }
  .output-comparison { grid-template-columns: 1fr; }
}

/* Pollo 参考页排版：首屏独立，SEO 内容从下一屏开始。 */
.pollo-layout { padding-bottom: 64px; }
.pollo-layout .first-screen { position: relative; width: calc(100vw - 48px); min-height: calc(100vh - 24px); margin-left: calc(50% - 50vw + 24px); padding: 26px 32px 30px; }
.pollo-layout .first-screen .breadcrumb { position: absolute; z-index: 3; top: 42px; left: 48px; width: 410px; margin: 0; padding: 0; }
.pollo-layout .first-screen .tool-hero { width: 100%; margin: 0; }
.pollo-layout .first-screen .tool-hero-info { max-width: 760px; margin: 0 auto; text-align: center; }
.pollo-layout .first-screen .tool-hero-info h1 { font-size: 36px; line-height: 1.32; }
.pollo-layout .first-screen .tool-hero-desc { max-width: 680px; margin-right: auto; margin-bottom: 0; margin-left: auto; }
.pollo-layout .first-screen .hero-chips { display: none; }
.pollo-layout .first-screen-grid { grid-template-columns: 468px minmax(0, 1fr); align-items: stretch; gap: 72px; width: 100%; max-width: 1392px; min-height: calc(100vh - 84px); }
.pollo-layout .first-screen .showcase-column { display: flex; min-width: 0; flex-direction: column; justify-content: center; gap: 28px; }
.pollo-layout .first-screen .action-column { display: flex; min-height: 0; padding-top: 54px; }
.pollo-layout .first-screen .tool-interface,
.pollo-layout .first-screen .login-prompt { width: 100%; min-height: 0; margin: 0; padding: 82px 20px 20px; border: 1px solid #dfe7eb; border-radius: 16px; background: rgba(255, 255, 255, .96); box-shadow: 0 14px 36px rgba(35, 55, 68, .07); }
.pollo-layout .first-screen .tool-interface { height: auto; }
.pollo-layout .first-screen .login-prompt { display: flex; align-items: center; justify-content: center; }
.pollo-layout .first-screen .showcase-column > .workspace-panel { width: min(710px, 100%); min-height: 0; margin: 0 auto; }
.pollo-layout .first-screen .demo-comparison { height: 406px; }
.pollo-layout .first-screen .processing-preview { height: 390px; }
.pollo-layout .first-screen .output-image { height: 360px; }
.pollo-layout .seo-content-start { margin-top: 0; padding-top: 112px; }
.pollo-layout .detail-section { max-width: 1180px; margin-bottom: 112px; }
.pollo-layout .section-heading { max-width: 800px; margin-bottom: 30px; }
.pollo-layout .section-heading h2,
.pollo-layout .detail-section > h2 { font-size: 40px; line-height: 1.4; }
.pollo-layout .section-heading p { font-size: 16px; }
.section-primary-cta { display: table; min-width: 190px; margin: 0 auto 48px; padding: 13px 28px; border: 1px solid #0a9c82; border-radius: 999px; color: #087d68; font-size: 15px; font-weight: 750; text-align: center; transition: color .2s, background .2s; }
.section-primary-cta:hover { color: #fff; background: #0a9c82; }
.landing-demo-comparison { position: relative; display: grid; grid-template-columns: 1fr 1fr; width: min(880px, 100%); height: 500px; margin: 0 auto 112px; overflow: hidden; border-radius: 18px; background: #edf1f3; box-shadow: 0 20px 55px rgba(31, 50, 63, .13); }
.landing-demo-half { min-width: 0; overflow: hidden; }
.landing-demo-half img { width: 100%; height: 100%; object-fit: cover; object-position: center 30%; }
.landing-demo-divider { position: absolute; top: 0; bottom: 0; left: 50%; width: 2px; background: rgba(255, 255, 255, .94); transform: translateX(-1px); }
.pollo-layout .feature-list { gap: 96px; margin-top: 0; }
.pollo-layout .feature-row { grid-template-columns: minmax(0, 480px) minmax(320px, 480px); justify-content: space-between; gap: 72px; }
.pollo-layout .feature-visual { height: 320px; border-radius: 16px; }
.pollo-layout .feature-copy h3 { font-size: 40px; line-height: 1.4; }
.pollo-layout .feature-copy p { font-size: 16px; line-height: 1.75; }
.pollo-layout .use-case-section { padding: 72px 56px; }
.use-case-tabs { display: flex; width: fit-content; max-width: 100%; justify-content: center; gap: 0; margin: -6px auto 0; }
.use-case-tabs button { position: relative; padding: 15px 22px 20px; border: 0; background: transparent; color: rgba(255, 255, 255, .66); font-size: 16px; white-space: nowrap; cursor: pointer; }
.use-case-tabs button::after { position: absolute; right: 0; bottom: 0; left: 0; height: 2px; background: transparent; content: ''; }
.use-case-tabs button.active { color: #fff; font-weight: 750; }
.use-case-tabs button.active::after { background: #64e9c1; }
.use-case-showcase { display: grid; grid-template-columns: 420px minmax(0, 1fr); align-items: center; gap: 32px; width: min(944px, 100%); min-height: 378px; margin: 0 auto; padding: 36px; border-radius: 24px; background: rgba(255, 255, 255, .07); }
.use-case-showcase__visual { height: 306px; overflow: hidden; border-radius: 14px; background: rgba(255, 255, 255, .06); }
.use-case-showcase__visual img { width: 100%; height: 100%; object-fit: cover; }
.use-case-showcase__visual .use-case-showcase__image--top { object-position: center top; }
.use-case-showcase__copy h3 { margin: 0 0 16px; color: #fff; font-size: 30px; line-height: 1.35; }
.use-case-showcase__copy p { margin: 0; color: rgba(255, 255, 255, .68); font-size: 16px; line-height: 1.75; }
.pollo-layout .how-to-section .steps { gap: 24px; }
.pollo-layout .how-to-section .step { min-height: 142px; padding: 30px; text-align: center; }
.pollo-layout .how-to-section .step-number { margin-right: auto; margin-left: auto; }
.pollo-layout .more-tools-section > h2 { margin-bottom: 44px; text-align: center; }
.pollo-layout .more-tools-section .grid-5 { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 16px; }
.pollo-layout .more-tools-section .grid-5 > :nth-child(n + 5) { display: none; }
.pollo-layout .faq-section { max-width: 960px; margin-right: auto; margin-bottom: 48px; margin-left: auto; padding: 74px 36px; border: 0; box-shadow: none; }
.pollo-layout .faq-section .section-heading { margin-right: auto; margin-left: auto; text-align: center; }
.pollo-layout .tool-bottom-how-to { margin-top: 0; }
.pollo-layout .faq-item { margin: 0; padding: 0 8px; border-width: 0 0 1px; border-radius: 0; background: transparent; }
.pollo-layout .faq-item summary { padding: 23px 42px 23px 28px; }
.pollo-layout .faq-item summary::after { right: auto; left: 0; background: transparent; color: #53606b; }
.pollo-layout .faq-item p { padding: 0 42px 22px 28px; }

@media (max-width: 1100px) {
  .pollo-layout .first-screen { min-height: auto; }
  .pollo-layout .first-screen .tool-hero { position: static; width: auto; margin: 34px auto 28px; }
  .pollo-layout .first-screen .breadcrumb { position: static; width: auto; margin: 0 auto; }
  .pollo-layout .first-screen-grid { grid-template-columns: 1fr; max-width: 760px; min-height: 0; }
  .pollo-layout .first-screen .action-column { padding-top: 0; }
  .pollo-layout .first-screen .tool-interface,
  .pollo-layout .first-screen .login-prompt { padding-top: 24px; }
  .pollo-layout .first-screen .showcase-column > .workspace-panel { margin-top: 0; }
}

@media (max-width: 768px) {
  .pollo-layout { padding-bottom: 32px; }
  .pollo-layout .first-screen { width: 100vw; margin-left: calc(50% - 50vw); padding: 20px 16px 30px; }
  .pollo-layout .first-screen .showcase-column { display: none; }
  .pollo-layout .first-screen .breadcrumb { margin-bottom: 16px; padding: 0 4px; }
  .pollo-layout .first-screen .tool-interface,
  .pollo-layout .first-screen .login-prompt { min-height: 530px; padding: 20px 12px; }
  .pollo-layout .seo-content-start { padding-top: 76px; }
  .pollo-layout .detail-section { margin-bottom: 76px; }
  .pollo-layout .section-heading { margin-bottom: 24px; }
  .pollo-layout .section-heading h2,
  .pollo-layout .detail-section > h2 { font-size: 28px; line-height: 1.42; }
  .section-primary-cta { margin-bottom: 34px; }
  .landing-demo-comparison { height: 320px; margin-bottom: 72px; border-radius: 12px; }
  .pollo-layout .feature-list { gap: 68px; }
  .pollo-layout .feature-row { gap: 22px; text-align: center; }
  .pollo-layout .feature-visual { height: 320px; order: 1; }
  .pollo-layout .feature-copy,
  .pollo-layout .feature-row:nth-child(even) .feature-copy { max-width: none; order: 2; }
  .pollo-layout .feature-copy h3 { margin: 10px 0 12px; font-size: 24px; }
  .pollo-layout .feature-copy p { margin-bottom: 20px; font-size: 15px; }
  .pollo-layout .use-case-section { padding: 48px 18px; }
  .use-case-tabs { justify-content: flex-start; overflow-x: auto; }
  .use-case-tabs button { flex: 0 0 auto; padding-right: 15px; padding-left: 15px; }
  .use-case-showcase { grid-template-columns: 1fr; gap: 24px; padding: 18px; text-align: center; }
  .use-case-showcase__visual { height: 270px; }
  .use-case-showcase__copy h3 { font-size: 24px; }
  .pollo-layout .more-tools-section .grid-5 { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
  .pollo-layout .faq-section { margin-bottom: 32px; padding: 48px 0; }
  .pollo-layout .faq-item summary { padding-right: 22px; }
}
</style>
